import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  ArrowLeft, MapPin, Clock, Eye, Heart, Tag, Phone, Envelope,
  User, ChatsCircle, Share, Warning, Check, CircleNotch,
  Tractor, Horse, Wrench, Leaf, CaretLeft, CaretRight, Storefront, HandHeart
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_ICONS = {
  machinery: Tractor,
  livestock: Horse,
  equipment: Wrench,
  feed: Leaf,
  land: MapPin,
  services: Tag,
  other: Tag
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const fetchListing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/marketplace/listings/${id}`);
      setListing(res.data);
      
      // Check if favorited
      if (token) {
        const favRes = await axios.get(`${API}/api/marketplace/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(favRes.data.some(l => l.id === id));
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Listing not found");
      } else {
        setError("Failed to load listing");
      }
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const toggleFavorite = async () => {
    if (!token) {
      toast.error("Please sign in to save favorites");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/api/marketplace/listings/${id}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(res.data.favorited);
      toast.success(res.data.favorited ? "Added to favorites" : "Removed from favorites");
    } catch (err) {
      toast.error("Failed to update favorites");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Please sign in to send messages");
      return;
    }
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/api/marketplace/messages`, {
        listing_id: id,
        content: messageContent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message sent!");
      setMessageContent("");
      setShowMessageForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const formatPrice = (price, currency = "EUR") => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCondition = (condition) => {
    if (!condition) return null;
    return condition.replace("_", " ").split(" ").map(w => 
      w.charAt(0).toUpperCase() + w.slice(1)
    ).join(" ");
  };

  const nextImage = () => {
    if (listing?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
    }
  };

  const prevImage = () => {
    if (listing?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
    }
  };

  const CategoryIcon = listing ? CATEGORY_ICONS[listing.category] || Tag : Tag;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-12">
          <div className="flex items-center justify-center">
            <CircleNotch size={32} className="animate-spin text-[#0F766E]" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-5xl mx-auto px-4 py-12 text-center">
          <Warning size={48} className="mx-auto text-[#8B8580] mb-4" />
          <h1 className="text-xl text-[#33302E] mb-2">{error}</h1>
          <Link to="/marketplace" className="text-[#0F766E] hover:underline">
            Back to Marketplace
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <Header />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/marketplace")}
          className="flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E] mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
          Back to Marketplace
        </button>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Image Gallery */}
            <div className="bg-white border border-[#E0D4C8] overflow-hidden">
              {listing.images && listing.images.length > 0 ? (
                <div className="relative">
                  <div className="aspect-[4/3] bg-[#F6E9DD]">
                    <img 
                      src={listing.images[currentImageIndex]} 
                      alt={listing.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  {listing.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white hover:bg-black/70 transition-colors"
                      >
                        <CaretLeft size={24} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white hover:bg-black/70 transition-colors"
                      >
                        <CaretRight size={24} />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {listing.images.map((img, i) => (
                          <button
                            key={img}
                            onClick={() => setCurrentImageIndex(i)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              i === currentImageIndex ? "bg-white" : "bg-white/50"
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="aspect-[4/3] bg-[#F6E9DD] flex items-center justify-center">
                  <CategoryIcon size={64} className="text-[#D1D5DB]" />
                </div>
              )}
              
              {/* Thumbnail strip */}
              {listing.images && listing.images.length > 1 && (
                <div className="flex gap-2 p-3 border-t border-[#E0D4C8] overflow-x-auto">
                  {listing.images.map((img, i) => (
                    <button
                      key={img}
                      onClick={() => setCurrentImageIndex(i)}
                      className={`flex-shrink-0 w-16 h-16 border-2 transition-colors ${
                        i === currentImageIndex ? "border-[#0F766E]" : "border-transparent"
                      }`}
                    >
                      <img src={img} alt={`Thumbnail ${i+1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white border border-[#E0D4C8] p-6">
              <h2 className="font-serif text-xl text-[#33302E] mb-4">Description</h2>
              <p className="text-[#6B6661] whitespace-pre-wrap leading-relaxed">
                {listing.description}
              </p>
            </div>

            {/* Details */}
            <div className="bg-white border border-[#E0D4C8] p-6">
              <h2 className="font-serif text-xl text-[#33302E] mb-4">Details</h2>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-[#6B6661]">Category</dt>
                  <dd className="text-[#33302E] capitalize">{listing.category}</dd>
                </div>
                {listing.subcategory && (
                  <div>
                    <dt className="text-sm text-[#6B6661]">Subcategory</dt>
                    <dd className="text-[#33302E]">{listing.subcategory}</dd>
                  </div>
                )}
                {listing.condition && (
                  <div>
                    <dt className="text-sm text-[#6B6661]">Condition</dt>
                    <dd className="text-[#33302E]">{formatCondition(listing.condition)}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm text-[#6B6661]">Location</dt>
                  <dd className="text-[#33302E]">{listing.location}, {listing.county}</dd>
                </div>
                <div>
                  <dt className="text-sm text-[#6B6661]">Listed</dt>
                  <dd className="text-[#33302E]">{formatDate(listing.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-sm text-[#6B6661]">Views</dt>
                  <dd className="text-[#33302E]">{listing.views}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price Card */}
            <div className="bg-white border border-[#E0D4C8] p-6 sticky top-4">
              {/* Listing Type Badge */}
              {listing.listing_type === "wanted" && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#FEF3C7] text-[#92400E] mb-4 -mx-6 -mt-6 border-b border-[#FCD34D]">
                  <HandHeart size={18} weight="fill" />
                  <span className="font-medium">Wanted</span>
                  <span className="text-sm ml-auto">Looking to buy</span>
                </div>
              )}
              
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-white capitalize ${
                    listing.category === "machinery" ? "bg-blue-600" :
                    listing.category === "livestock" ? "bg-green-600" :
                    listing.category === "equipment" ? "bg-orange-600" :
                    listing.category === "feed" ? "bg-lime-600" :
                    listing.category === "land" ? "bg-amber-700" :
                    "bg-gray-600"
                  }`}>
                    <CategoryIcon size={12} />
                    {listing.category}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#6B6661]">
                  <Eye size={16} />
                  {listing.views}
                </div>
              </div>

              <h1 className="font-serif text-2xl text-[#33302E] mb-3" data-testid="listing-title">
                {listing.title}
              </h1>

              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-bold text-[#0F766E]" data-testid="listing-price">
                  {listing.listing_type === "wanted" ? "Budget: " : ""}
                  {formatPrice(listing.price, listing.currency)}
                </span>
                {listing.negotiable && (
                  <span className="text-sm text-[#6B6661]">Negotiable</span>
                )}
              </div>

              <div className="flex items-center gap-2 text-[#6B6661] mb-6">
                <MapPin size={16} />
                <span>{listing.location}, {listing.county}</span>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {user?.id !== listing.user_id ? (
                  <>
                    <button
                      onClick={() => setShowMessageForm(!showMessageForm)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#0F766E] text-white hover:bg-[#0D6B64] transition-colors"
                      data-testid="contact-btn"
                    >
                      <ChatsCircle size={20} />
                      {listing.listing_type === "wanted" ? "Contact Buyer" : "Contact Seller"}
                    </button>
                    
                    {showMessageForm && (
                      <form onSubmit={sendMessage} className="mt-4 space-y-3">
                        <textarea
                          value={messageContent}
                          onChange={(e) => setMessageContent(e.target.value)}
                          placeholder={listing.listing_type === "wanted" 
                            ? "Hi, I have what you're looking for..."
                            : "Hi, I'm interested in this listing..."}
                          rows={4}
                          required
                          className="w-full px-4 py-3 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E] resize-none"
                          data-testid="message-input"
                        />
                        <button
                          type="submit"
                          disabled={sendingMessage || !messageContent.trim()}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#022C22] text-white hover:bg-[#064E3B] disabled:opacity-50 transition-colors"
                        >
                          {sendingMessage ? (
                            <CircleNotch size={18} className="animate-spin" />
                          ) : (
                            <Check size={18} />
                          )}
                          Send Message
                        </button>
                      </form>
                    )}
                  </>
                ) : (
                  <Link
                    to={`/marketplace/edit/${id}`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#0F766E] text-white hover:bg-[#0D6B64] transition-colors"
                  >
                    Edit Listing
                  </Link>
                )}

                <button
                  onClick={toggleFavorite}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 border transition-colors ${
                    isFavorite 
                      ? "border-red-300 bg-red-50 text-red-600 hover:bg-red-100" 
                      : "border-[#E0D4C8] text-[#6B6661] hover:bg-[#F6E9DD]"
                  }`}
                  data-testid="favorite-btn"
                >
                  <Heart size={20} weight={isFavorite ? "fill" : "regular"} />
                  {isFavorite ? "Saved" : "Save"}
                </button>
              </div>

              {/* Contact Info */}
              {(listing.contact_phone || listing.contact_email) && (
                <div className="mt-6 pt-6 border-t border-[#E0D4C8]">
                  <h3 className="font-medium text-[#33302E] mb-3">Contact Information</h3>
                  <div className="space-y-2">
                    {listing.contact_phone && (
                      <a 
                        href={`tel:${listing.contact_phone}`}
                        className="flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E]"
                      >
                        <Phone size={16} />
                        {listing.contact_phone}
                      </a>
                    )}
                    {listing.contact_email && (
                      <a 
                        href={`mailto:${listing.contact_email}`}
                        className="flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E]"
                      >
                        <Envelope size={16} />
                        {listing.contact_email}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Seller Info */}
              <div className="mt-6 pt-6 border-t border-[#E0D4C8]">
                <h3 className="font-medium text-[#33302E] mb-3">Posted By</h3>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#E5E7EB] rounded-full flex items-center justify-center">
                    <User size={20} className="text-[#6B6661]" />
                  </div>
                  <div>
                    <p className="text-[#33302E]">{listing.user_email.split('@')[0]}</p>
                    <p className="text-sm text-[#6B6661]">Member</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
